import {
  DatePicker,
  Field,
  Input,
  RadioGroup,
  Spin,
} from '@/components/commons';
import { calendarPostStatus, modulesReactQuill } from '@/utils';
import { Badge, Button, Form, Image, Upload } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Moment } from 'moment';
import { useFormContext } from 'react-hook-form';
import { CloseOutlined } from '@ant-design/icons';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import { CarouselRef } from 'antd/es/carousel';
import { useRef } from 'react';
import { ContentImage } from '../../CalendarPostList';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const FontAttributor: any = Quill.import('formats/font');
FontAttributor.whitelist = ['Noto Sans JP', 'Arial', 'Times New Roman'];
Quill.register(FontAttributor, true);

const AlignStyle: any = Quill.import('attributors/style/align');
Quill.register(AlignStyle, true);

interface IProps {
  isLoading: boolean;
  openDrawer: boolean;
}

export const CalendarPostForm: React.FC<IProps> = ({
  isLoading,
  openDrawer,
}) => {
  const { trigger, watch, setValue, setError, clearErrors } = useFormContext();
  const carouselRef = useRef<CarouselRef | null>(null);

  const disabledDate = (current: Dayjs) => {
    return current.isBefore(dayjs().startOf('day'));
  };

  const disabledTime = (current: Dayjs) => {
    if (!current) return {};

    const now = dayjs();
    const selectedDate = dayjs(current);

    if (selectedDate.isBefore(now, 'day')) {
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }

    if (selectedDate.isSame(now, 'day')) {
      const disabledHours: number[] = [];
      for (let i = 0; i < now.hour(); i++) {
        disabledHours.push(i);
      }

      const disabledMinutes = (selectedHour: number) => {
        if (selectedHour === now.hour()) {
          const minutes = [];
          for (let i = 0; i <= now.minute(); i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      };

      return {
        disabledHours: () => disabledHours,
        disabledMinutes: disabledMinutes,
      };
    }

    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  };

  const isDisabled = watch('isDisabled');
  const images: Array<ContentImage | null> = watch('images');
  const icons: Array<ContentImage | null> = watch('icons');

  const goToSlide = (index: number) => {
    const carousel = carouselRef.current;
    if (carousel) {
      setTimeout(() => {
        carousel.goTo(index);
      }, 0);
    }
  };

  const handleAddImage = () => {
    if (images?.length < 6) {
      setValue('images', [...images, null]);
      goToSlide(images.length);
    }
  };

  const handleRemoveImage = (index: number) => {
    const data = [...images];
    data[index] = null;
    setValue('images', data);
  };

  const handleRemoveIcon = (index: number) => {
    const data = [...icons];
    data[index] = null;
    setValue('icons', data);
  };

  const handleUpload = (file: File, index: number) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new (window.Image as { new (): HTMLImageElement })();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = Math.min(img.width, img.height);
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(
            img,
            (img.width - size) / 2,
            (img.height - size) / 2,
            size,
            size,
            0,
            0,
            size,
            size,
          );

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const newFile = new File([blob], `image-${index}.jpeg`, {
                  type: 'image/jpeg',
                });

                const scaledImage = URL.createObjectURL(blob);
                const data = [...images];
                data[index] = {
                  previewUrl: scaledImage,
                  file: newFile,
                };
                setValue('images', data);
              }
            },
            'image/jpeg',
            0.8, // Định dạng và chất lượng (từ 0 đến 1)
          );
        }
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleUploadIcon = (file: File, index: number) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new (window.Image as { new (): HTMLImageElement })();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = Math.min(img.width, img.height);
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(
            img,
            (img.width - size) / 2,
            (img.height - size) / 2,
            size,
            size,
            0,
            0,
            size,
            size,
          );

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const newFile = new File([blob], `image-${index}.jpeg`, {
                  type: 'image/jpeg',
                });

                const scaledImage = URL.createObjectURL(blob);
                const data = [...icons];
                data[index] = {
                  previewUrl: scaledImage,
                  file: newFile,
                };
                setValue('icons', data);
              }
            },
            'image/jpeg',
            0.8, // Định dạng và chất lượng (từ 0 đến 1)
          );
        }
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  const beforeUpload = (file: File, index: number) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (file.size > FILE_SIZE_LIMIT) {
      setError('attachment', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('attachment', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }
    clearErrors('attachment');
    handleUpload(file, index);

    return true;
  };

  const beforeUploadIcon = (file: File, index: number) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (file.size > FILE_SIZE_LIMIT) {
      setError('iconAttachment', {
        type: 'validate',
        message: 'アイコンのサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('iconAttachment', {
        type: 'validate',
        message: 'アイコンは .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }
    clearErrors('iconAttachment');
    handleUploadIcon(file, index);

    return true;
  };

  const handleDeleteImage = (index: number) => {
    if (images?.length !== 1) {
      const data = [...images];
      data.splice(index, 1);
      setValue('images', data);
      goToSlide(index);
    }
  };

  const handleDeleteIcon = (index: number) => {
    if (icons?.length !== 1) {
      const data = [...icons];
      data.splice(index, 1);
      setValue('icons', data);
      goToSlide(index);
    }
  };

  return (
    <Form layout='vertical'>
      <Spin spinning={isLoading}>
        <label className='title-calendar font-semibold '>
          アイコン
          <Badge status='error' className='ml-2 mb-1' />
        </label>
        <div
          style={{
            display: 'flex',
            overflowX: 'auto',
            maxWidth: '800px',
            whiteSpace: 'nowrap',
            minHeight: '150px',
          }}
        >
          {icons?.map((value, index) => {
            return !value ? (
              <Badge
                className='mr-4 mt-3'
                key={`icon-${index}`}
                count={
                  <Button
                    type='primary'
                    shape='circle'
                    icon={<CloseOutlined />}
                    size='small'
                    className={`bg-red-500 border-none hidden`}
                    onClick={() => handleDeleteIcon(index)}
                  />
                }
              >
                <div className='border-solid rounded border-2 border-zinc-300  w-[120px] h-[120px] flex justify-center items-center'>
                  <Upload
                    key={'icon'}
                    beforeUpload={(file: File) => beforeUploadIcon(file, index)}
                    customRequest={() => {}}
                    listType='text'
                    maxCount={1}
                    showUploadList={false}
                  >
                    <Button
                      type='default'
                      shape='default'
                      size='middle'
                      className='text-[#84ab42] border border-[#84ab42] font-semibold'
                      disabled={isDisabled}
                    >
                      画像を選択
                    </Button>
                  </Upload>
                </div>
              </Badge>
            ) : (
              <div className='relative inline-block mr-4 mt-3' key={index}>
                <Image
                  width={120}
                  height={120}
                  src={value?.previewUrl}
                  className='block rounded-lg'
                  fallback={FALLBACK_URL}
                />
                <Button
                  type='primary'
                  shape='circle'
                  icon={<CloseOutlined />}
                  size='small'
                  onClick={() => handleRemoveIcon(index)}
                  className={`absolute -top-2 -right-2 z-10 bg-red-500 border-none ${isDisabled && 'hidden'}`}
                />
              </div>
            );
          })}
        </div>
        <label className='title-calendar font-medium text-xs'>
          <span className='font-bold mr-1'>※</span>
          1:1の写真（正方形）をアップロードしてください
        </label>
        <Field name='iconAttachment' className='mt-[-34px] absolute'>
          <></>
        </Field>
        <Field label='詳細画面表示タイトル' name='titleContent' className='mt-5' required>
          <Input
            placeholder='詳細画面表示タイトル'
            maxLength={100}
            disabled={isDisabled}
          />
        </Field>
        <Field label='コンテンツ名' name='headerTitle' className='mt-5' required>
          <Input
            placeholder='コンテンツ名'
            maxLength={100}
            disabled={isDisabled}
          />
        </Field>
        <Field label='コンテンツ見出し' name='title' required>
          <Input
            placeholder='コンテンツ見出し'
            maxLength={100}
            disabled={isDisabled}
          />
        </Field>
        <Field label='本文' name='content' required>
          <ReactQuill
            readOnly={isDisabled}
            theme='snow'
            modules={modulesReactQuill}
          />
        </Field>
        <label className='title-calendar font-semibold'>
          配信日時
          <Badge status='error' className='ml-2' />
        </label>
        <div className='flex items-baseline	gap-1 pt-2'>
          <Field name='publicAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue('publicAt', e ? (e as Moment).second(0) : undefined);
                trigger('eventFrom');
                trigger('publicAt');
              }}
              disabled={isDisabled}
            />
          </Field>
        </div>
        <label className='title-calendar font-semibold '>
          コンテンツ画像
          <Badge status='error' className='ml-2 mb-1' />
        </label>
        <div
          style={{
            display: 'flex',
            overflowX: 'auto',
            maxWidth: '800px',
            whiteSpace: 'nowrap',
            minHeight: '150px',
          }}
        >
          {images?.map((value, index) => {
            return !value ? (
              <Badge
                className='mr-4 mt-3'
                key={index}
                count={
                  <Button
                    type='primary'
                    shape='circle'
                    icon={<CloseOutlined />}
                    size='small'
                    className={`bg-red-500 border-none hidden`}
                    onClick={() => handleDeleteImage(index)}
                  />
                }
              >
                <div className='border-solid rounded border-2 border-zinc-300  w-[120px] h-[120px] flex justify-center items-center'>
                  <Upload
                    beforeUpload={(file: File) => beforeUpload(file, index)}
                    customRequest={() => {}}
                    listType='text'
                    maxCount={1}
                    showUploadList={false}
                  >
                    <Button
                      type='default'
                      shape='default'
                      size='middle'
                      className='text-[#84ab42] border border-[#84ab42] font-semibold'
                      disabled={isDisabled}
                    >
                      画像を選択
                    </Button>
                  </Upload>
                </div>
              </Badge>
            ) : (
              <div className='relative inline-block mr-4 mt-3' key={index}>
                <Image
                  width={120}
                  height={120}
                  src={value?.previewUrl}
                  className='block rounded-lg'
                  fallback={FALLBACK_URL}
                />
                <Button
                  type='primary'
                  shape='circle'
                  icon={<CloseOutlined />}
                  size='small'
                  onClick={() => handleRemoveImage(index)}
                  className={`absolute -top-2 -right-2 z-10 bg-red-500 border-none ${isDisabled && 'hidden'}`}
                />
              </div>
            );
          })}
        </div>
        <label className='title-calendar font-medium text-xs'>
          <span className='font-bold mr-1'>※</span>
          1:1の写真（正方形）をアップロードしてください
        </label>
        <Field name='attachment' className='mt-[-34px] absolute'>
          <></>
        </Field>
        <Field label='ホーム発話' name='talk' className='mt-6' required>
          <Input
            placeholder='ホーム発話'
            maxLength={100}
            disabled={isDisabled}
          />
        </Field>
        <Field label='タイムライン' name='timeLine' required>
          <Input
            placeholder='タイムライン'
            maxLength={100}
            disabled={isDisabled}
          />
        </Field>
        <Field label='プッシュ通知' name='message' required>
          <Input
            placeholder='プッシュ通知'
            maxLength={100}
            disabled={isDisabled}
          />
        </Field>
        <Field label='ステータス' name='status' className='w-full'>
          <RadioGroup options={calendarPostStatus} disabled={isDisabled} />
        </Field>
      </Spin>
    </Form>
  );
};
